import type { InternalUrl, Query, Routing, TransitionOptions } from '@tbitop/routing';
import { toInternalUrl } from '@tbitop/routing';
import { omit } from 'lodash-es';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams as useReactRouterParams } from 'react-router-dom';
import { Link } from './link';
import { toLocationDescriptor } from './location-descriptor';

const handleScrollOption = (options?: TransitionOptions) => {
  if (options?.scroll) {
    window.scrollTo(0, 0);
  }
};

export const useMobileRouting = (): Routing => {
  const location = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    const { pathname, search } = location;

    const simplifiedQuery = Object.fromEntries(new URLSearchParams(search));

    const transition = (method: 'push' | 'replace', url: string | InternalUrl, options?: TransitionOptions) => {
      navigate(toLocationDescriptor(url), { replace: method === 'replace' });
      handleScrollOption(options);
    };

    return {
      Link,

      pathname,
      query: simplifiedQuery,

      back() {
        navigate(-1);
      },

      push(url, options) {
        transition('push', url, options);
      },

      pushWithAdditionalQueryParams(params, options) {
        transition('push',
          {
            query: { ...simplifiedQuery, ...params },
          },
          options);
      },

      pushWithoutQueryParams(paramNames, options) {
        transition('push',
          {
            query: omit(simplifiedQuery, ...paramNames),
          },
          options);
      },

      replace(url, options) {
        transition('replace', url, options);
      },

      replaceWithAdditionalQueryParams(params: Query, options) {
        transition('replace',
          {
            query: { ...simplifiedQuery, ...params },
          },
          options);
      },

      replaceWithoutQueryParams(paramNames, options) {
        transition('replace',
          {
            query: omit(simplifiedQuery, ...paramNames),
          },
          options);
      },

      isActiveRoute: url => pathname.startsWith(toInternalUrl(url).pathname ?? ''),
    } as Routing;

  }, [location, navigate]);
};

export const useParams = () => useReactRouterParams<Record<string, string>>();
